import React, { useState, useEffect, useCallback } from 'react';
import { Button, Modal, Card, Form } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory, {
	PaginationProvider,
	PaginationListStandalone,
	SizePerPageDropdownStandalone,
} from 'react-bootstrap-table2-paginator';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import { Fetcher } from "../../../Helpers/fetcher.js";
import { Notify } from "../../../Helpers/notification.js";
import Cookies from 'js-cookie';

import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css';
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';

const SearchDialer = ({ app }) => {
	const [dialers, setDialers] = useState([]);
	const [selectedDialer, setSelectedDialer] = useState('');
	const [groups, setGroups] = useState([]);
	const [sizePerPage, setSizePerPage] = useState(10);
	const [groupModal, setGroupModal] = useState({ show: false, data: null });
	const [loading, setLoading] = useState(false);

	const fetchDialers = useCallback(async () => {
		try {
			setLoading(true);
			const response = await Fetcher.get(`${app.apiPath}/api/obd/dialers`);
			setDialers(response || []);
		} catch (err) {
			console.error('Error fetching dialers:', err);
			Notify.notify('Failed to fetch dialers');
		} finally {
			setLoading(false);
		}
	}, []);

	const fetchGroups = useCallback(async (dialerId) => {
		if (!dialerId) return;

		try {
			setLoading(true);
			const url = `${app.apiPath}/api/obd/dialer/group/${dialerId}`;
			const response = await Fetcher.get(url);

			if (response.error) {
				throw new Error(response.error);
			}

			setGroups(Array.isArray(response) ? response : [response]);
		} catch (err) {
			console.error('Error fetching groups:', err);
			Notify.notify(err.message || 'Failed to fetch groups');
			setGroups([]);
		} finally {
			setLoading(false);
		}
	}, [app.apiPath]);

	useEffect(() => {
		const savedSizePerPage = Cookies.get('dialerGroupsSizePerPage');
		if (savedSizePerPage) {
			setSizePerPage(Number(savedSizePerPage));
		}
		fetchDialers();
	}, [fetchDialers]);

	const handleSizePerPageChange = (newSize) => {
		setSizePerPage(newSize);
		Cookies.set('dialerGroupsSizePerPage', newSize);
	};

	const handleSearch = () => {
		if (!selectedDialer) {
			Notify.notify('Please select a dialer');
			return;
		}
		fetchGroups(selectedDialer);
	};

	const columns = [
		{
			dataField: 'dialerName',
			text: 'Dialer Name',
			sort: true
		},
		{
			dataField: 'groupName',
			text: 'Group Name',
			formatter: (_, row) => (
				<Button
					variant="primary"
					size="sm"
					onClick={() => setGroupModal({ show: true, data: row.groupName })}
				>
					View Group
				</Button>
			)
		}
	];

	const defaultSorted = [{ dataField: 'dailerName', order: 'asc' }];
	const paginationOptions = {
		custom: true,
		totalSize: groups.length,
		sizePerPage,
		sizePerPageList: [10, 25, 50, 100],
		onSizePerPageChange: handleSizePerPageChange
	};

	return (
		<Card>
			<Card.Header>Dialer Groups</Card.Header>
			<Card.Body>
				<Form className="d-flex gap-3 mb-3 align-items-end">
					<Form.Group controlId="dialerSelect" >
						<Form.Label>Select Dialer</Form.Label>
						<Form.Select
							value={selectedDialer}
							onChange={(e) => setSelectedDialer(e.target.value)}
							disabled={loading}
						>
							<option value="">-- Select Dialer --</option>
							{dialers.map(dialer => (
								<option key={dialer.dialerId} value={dialer.dialerId}>
									{dialer.dialerName}
								</option>
							))}
						</Form.Select>

					</Form.Group>
					<Button
						variant="primary"
						onClick={handleSearch}
						disabled={loading || !selectedDialer}
						className="mb-3"
					>
						{loading ? 'Loading...' : 'Search'}
					</Button>
				</Form>

				<PaginationProvider pagination={paginationFactory(paginationOptions)}>
					{({ paginationProps, paginationTableProps }) => (
						<ToolkitProvider
							keyField="id"
							data={groups}
							columns={columns}
							search
							bootstrap4
						>
							{props => (
								<>
									<div className="d-flex justify-content-between align-items-center mb-2">
										<div>
											Show <SizePerPageDropdownStandalone className='search-dropdown' {...paginationProps} />
										</div>
									</div>
									<BootstrapTable
										bordered
										hover
										condensed
										wrapperClasses="table-responsive"
										classes="dialer-groups-table"
										defaultSorted={defaultSorted}
										{...props.baseProps}
										{...paginationTableProps}
										noDataIndication="No groups found"
									/>
									<PaginationListStandalone {...paginationProps} />
								</>
							)}
						</ToolkitProvider>
					)}
				</PaginationProvider>

				<Modal
					show={groupModal.show}
					onHide={() => setGroupModal({ show: false, data: null })}
					size="lg"
				>
					<Modal.Header closeButton>
						<Modal.Title>Group Details</Modal.Title>
					</Modal.Header>
					<Modal.Body style={{ maxHeight: '400px' }}>
						{groupModal.data ? (
							<div>
								{groupModal.data.split(',').map((group, index) => (
									<div style={{
										fontFamily: 'monospace',
										whiteSpace: 'pre-wrap',
										wordBreak: 'break-word',
										maxWidth: '100%',
										overflowX: 'auto',
									}} key={index} className="mb-2 p-2 border-bottom">
										{group.trim()}
									</div>
								))}
							</div>
						) : (
							<p>No group data available</p>
						)}
					</Modal.Body>
					<Modal.Footer>
						<Button variant="secondary" onClick={() => setGroupModal({ show: false, data: null })}>
							Close
						</Button>
					</Modal.Footer>
				</Modal>
			</Card.Body>
		</Card>
	);
};

export default SearchDialer;